/**
 * Tenant theming — maps StorefrontConfig.theme tokens onto the storefront's CSS
 * variables (defined in globals.css `:root`).
 *
 * Theming is injected as a server-rendered `<style>:root{…}</style>` so the values
 * are present in the first painted HTML (no FOUC) and apply globally — including to
 * out-of-tree elements like sonner toasts, modals and sticky chrome (which read the
 * same `:root` vars), avoiding split-theme bugs.
 */
import type { ThemeColorKey, ThemeTokens } from "@/src/types/storefront";

const COLOR_VAR: Record<ThemeColorKey, string> = {
  coral: "--coral",
  coralDeep: "--coral-deep",
  navy: "--navy",
  gold: "--gold",
  turmeric: "--turmeric",
  cream: "--cream",
  surface: "--surface",
  border: "--border",
};

const FONT_BODY: Record<NonNullable<ThemeTokens["fontPreset"]>, string> = {
  jakarta: "var(--font-jakarta), -apple-system, BlinkMacSystemFont, sans-serif",
  serif: "var(--font-serif), Georgia, 'Times New Roman', serif",
  mono: "var(--font-mono), ui-monospace, SFMono-Regular, monospace",
};

const RADIUS: Record<
  NonNullable<ThemeTokens["cornerRadius"]>,
  Record<string, string>
> = {
  sharp: { "--radius-sm": "4px", "--radius": "6px", "--radius-lg": "8px", "--radius-xl": "10px" },
  rounded: { "--radius-sm": "10px", "--radius": "14px", "--radius-lg": "20px", "--radius-xl": "28px" },
  pill: { "--radius-sm": "14px", "--radius": "20px", "--radius-lg": "28px", "--radius-xl": "40px" },
};

const HEX = /^#([0-9a-fA-F]{6})$/;

/** Resolve theme tokens into a CSS-variable record (e.g. { "--coral": "#fff" }). */
export function resolveThemeVars(theme?: ThemeTokens | null): Record<string, string> {
  const vars: Record<string, string> = {};
  if (!theme) return vars;

  if (theme.colors) {
    for (const [key, value] of Object.entries(theme.colors)) {
      const cssVar = COLOR_VAR[key as ThemeColorKey];
      if (cssVar && typeof value === "string" && HEX.test(value)) {
        vars[cssVar] = value;
      }
    }
  }
  if (theme.fontPreset && FONT_BODY[theme.fontPreset]) {
    vars["--font-body"] = FONT_BODY[theme.fontPreset];
  }
  if (theme.cornerRadius && RADIUS[theme.cornerRadius]) {
    Object.assign(vars, RADIUS[theme.cornerRadius]);
  }
  return vars;
}

/**
 * Build a `:root{…}` CSS string for a server-rendered <style> tag.
 * Returns null when there are no overrides (so the default theme stands untouched).
 */
export function buildThemeStyle(theme?: ThemeTokens | null): string | null {
  const vars = resolveThemeVars(theme);
  const keys = Object.keys(vars);
  if (keys.length === 0) return null;
  const body = keys.map((k) => `${k}:${vars[k]}`).join(";");
  return `:root{${body}}`;
}
